import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import { StyledPageTitle } from 'theme/styles';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import { useNavigate } from 'react-router-dom';
import { Loader } from 'components/atoms/Loader';
import { useLicenceAuthorities } from './hooks';
import { EPerPages, ISortOptions } from 'models/table';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Table } from 'components/molecules/Table';
import { CardList } from 'components/molecules/CardList';
import { Pagination } from 'components/molecules/Pagination';
import { licenceAuthorityTableColumns, mapLicenceAuthorityToDataSource } from './helpers';
import LicenceAuthoritiesTableCard from './LicenceAuthoritiesTableCard';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';

const LicenceAuthoritiesPage = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { isDesktop } = useThemeBreakpoint();
  const [isLoading, setIsLoading] = useState(true);
  const { getLicenceAuthoritiesData, licenceAuthorities, paginator } = useLicenceAuthorities();
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<EPerPages>(EPerPages.perPage25);

  useEffect(() => {
    (async () => {
      if (!licenceAuthorities.length) {
        setIsLoading(true);
      }
      await getLicenceAuthoritiesData(currentPage, perPage, sort);
      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, sort]);

  const viewAction = (id: string) =>
    navigate(ERouteLinks.DetailsLicenceAuthorities.replace(':id', id));
  const editAction = (id: string) =>
    navigate(ERouteLinks.EditLicenceAuthorities.replace(':id', id));

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.administration.name'), route: ERouteLinks.Administration },
          t('licenceAuthorities.header'),
        ]}
      />
      <StyledPageTitle variant="h4">{t('licenceAuthorities.header')}</StyledPageTitle>

      <Grid container size={{ xs: 12 }} sx={{ my: '20px', justifyContent: 'flex-end' }}>
        <Button
          id={`licenceAuthorities_add-button`}
          label={t('licenceAuthorities.addNewLicenceAuthority.header')}
          icon={EIconTypes.plus}
          variant={EButtonVariants.contained}
          onClick={() => navigate(ERouteLinks.AddLicenceAuthorities)}
        />
      </Grid>
      {isLoading ? (
        <Loader isLoading={isLoading} />
      ) : (
        <>
          {isDesktop ? (
            <Table
              columns={licenceAuthorityTableColumns({ t, viewAction, editAction })}
              dataSource={mapLicenceAuthorityToDataSource(licenceAuthorities)}
              onSort={setSort}
            />
          ) : (
            <CardList
              items={mapLicenceAuthorityToDataSource(licenceAuthorities)}
              render={(data, index) => (
                <LicenceAuthoritiesTableCard
                  data={data}
                  key={index}
                  viewAction={viewAction}
                  editAction={editAction}
                />
              )}
            />
          )}
        </>
      )}

      {paginator && (
        <Pagination
          count={paginator.totalElements}
          onChangePage={(page) => setCurrentPage(page)}
          current={currentPage}
          perPage={perPage}
          onChangePerPage={(value) => setPerPage(value)}
          isLoading={false}
        />
      )}
    </>
  );
};

export default LicenceAuthoritiesPage;
